import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Ruler } from 'lucide-react';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import ColumnSettingsModal from '../../components/common/ColumnSettingsModal';
import ActionMenu from '../../components/common/ActionMenu';
import exportReportExcel from '../reports/export/reportExcelExporter';
import { utilitiesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useDataTransfer } from '../../context/DataTransferContext';

import { applyColumnFilters } from '../../utils/listSearch';
const DEFAULT_TARGET = '101/2/C.0/18.0';

const fmtDist = (v) => (v == null ? 'N/A' : `${Number(v).toFixed(1)}`);

const DistanceApproxPage = () => {
  const { notify } = useNotifications();
  const { trackTransfer } = useDataTransfer();

  const [targetInput, setTargetInput]   = useState(DEFAULT_TARGET);
  const [appliedTarget, setAppliedTarget] = useState(DEFAULT_TARGET);
  const [targetMeta, setTargetMeta]     = useState(null);
  const [rows, setRows]                 = useState([]);
  const [loading, setLoading]           = useState(false);

  const [filters, setFilters]           = useState({});
  const [columnOrder, setColumnOrder]   = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [showColSettings, setShowColSettings] = useState(false);

  const loadData = useCallback(async () => {
    if (!appliedTarget || !appliedTarget.trim()) return;
    setLoading(true);
    try {
      const res = await utilitiesApi.getDistanceApprox(appliedTarget.trim());
      setRows(res.data.data || []);
      setTargetMeta(res.data.target || null);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to load distance data');
    } finally {
      setLoading(false);
    }
  }, [appliedTarget, notify]);

  useEffect(() => { loadData(); }, [loadData]);

  const submitTarget = () => {
    const t = targetInput.trim();
    if (!t) return;
    setAppliedTarget(t);
  };

  const columns = useMemo(() => ([
    { key: 'panel',       label: 'Panel',       width: 220, alignment: 'left' },
    { key: 'panelType',   label: 'Type',        width: 60,  alignment: 'left' },
    { key: 'parent',      label: 'Parent',      width: 180, alignment: 'left', settingsLabel: 'Panel Info — Parent' },
    { key: 'description', label: 'Description', width: 260, alignment: 'left', settingsLabel: 'Panel Info — Description' },
    { key: 'location',    label: 'Location',    width: 140, alignment: 'left', settingsLabel: 'Location — Full' },
    { key: 'bldg',        label: 'Bldg',        width: 60,  alignment: 'right', settingsLabel: 'Location — Bldg' },
    { key: 'floor',       label: 'Floor',       width: 72,  alignment: 'right', settingsLabel: 'Location — Floor' },
    { key: 'column',      label: 'Column',      width: 82,  alignment: 'right', settingsLabel: 'Location — Column' },
    { key: 'grid',        label: 'Grid',        width: 60,  alignment: 'right', settingsLabel: 'Location — Grid' },
    { key: 'floorDist',   label: 'Floor',       width: 70,  alignment: 'right', settingsLabel: 'Distances — Floor',  formatter: fmtDist },
    { key: 'columnDist',  label: 'Column',      width: 82,  alignment: 'right', settingsLabel: 'Distances — Column', formatter: fmtDist },
    { key: 'gridDist',    label: 'Grid',        width: 70,  alignment: 'right', settingsLabel: 'Distances — Grid',   formatter: fmtDist },
    { key: 'approxDist',  label: 'Approx',      width: 78,  alignment: 'right', settingsLabel: 'Distances — Approx', formatter: fmtDist },
  ]), []);

  const sectionGroups = useMemo(() => ([
    { label: 'Panel', keys: ['panel', 'panelType', 'parent', 'description'] },
    { label: 'Location',  keys: ['location', 'bldg', 'floor', 'column', 'grid'] },
    { label: 'Distances (ft)', keys: ['floorDist', 'columnDist', 'gridDist', 'approxDist'] },
  ]), []);

  const filteredRows = useMemo(() => applyColumnFilters(rows, filters), [rows, filters]);

  // Row id — panel is unique
  const gridRows = useMemo(() => rows.map(r => ({ ...r, id: r.panel })), [rows]);
  const gridFilteredRows = useMemo(() => filteredRows.map(r => ({ ...r, id: r.panel })), [filteredRows]);

  const handleDownload = async () => {
    if (gridFilteredRows.length === 0) {
      notify.warning('No data to export');
      return;
    }
    const hiddenSet = new Set(hiddenColumns);
    const activeCols = (columnOrder.length > 0
      ? columnOrder.map(k => columns.find(c => c.key === k)).filter(Boolean)
      : columns
    ).filter(c => !hiddenSet.has(c.key));
    const exportCols = activeCols.map(c => ({
      field: c.key,
      label: c.settingsLabel || c.label,
      align: c.alignment,
    }));
    try {
      await trackTransfer(
        { name: `Distance Approximation from ${appliedTarget}`, format: 'excel', rows: gridFilteredRows.length },
        async () => {
          await exportReportExcel({
            config: { reportName: `Distance Approximation from ${appliedTarget}` },
            columns: exportCols,
            data: gridFilteredRows,
            isCancelled: () => false,
            onProgress: () => {},
          });
        }
      );
      notify.success('Download complete');
    } catch (err) {
      notify.error(`Download failed: ${err.message || err}`);
    }
  };

  const handleColSettingsApply = ({ columnOrder: order, hiddenColumns: hidden }) => {
    setColumnOrder(order);
    setHiddenColumns(hidden);
  };

  const nearest = gridFilteredRows.find(r => r.approxDist != null);

  return (
    <div className="stui-module-container">

      {/* HEADER */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Ruler size={20} />
          <h1>Distance Approximation</h1>
          <span className="stui-module-header-meta">
            {loading
              ? 'Loading…'
              : nearest
                ? `Nearest: ${nearest.panel} @ ${fmtDist(nearest.approxDist)} ft`
                : `${gridFilteredRows.length} panel${gridFilteredRows.length !== 1 ? 's' : ''}`
            }
          </span>
          {targetMeta && !targetMeta.buildingConfigured && (
            <span className="stui-module-header-meta" style={{ color: 'var(--color-warning-text)' }}>
              ⚠ Building "{targetMeta.bldg}" not configured — no distances will compute
            </span>
          )}
        </div>
        <div className="stui-module-header-actions">

          {/* Target input */}
          <div className="stui-field-group stui-field-inline">
            <label>Target</label>
            <input
              type="text"
              className="stui-field-input stui-mono"
              style={{ width: 160 }}
              placeholder="Bldg/Floor/Col/Grid"
              value={targetInput}
              onChange={e => setTargetInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitTarget(); }}
            />
            <button
              className="stui-btn stui-btn-secondary"
              onClick={submitTarget}
              disabled={loading || !targetInput.trim() || targetInput.trim() === appliedTarget}
            >
              Compute
            </button>
          </div>

          {/* 6-dot menu */}
          <ActionMenu
            onRefresh={loadData}
            onModifyView={() => setShowColSettings(true)}
            showModifyView={true}
            onDownload={handleDownload}
          />
        </div>
      </div>

      {/* GRID */}
      <div className="stui-datasheet-view">
        <FilterableDataGrid
          data={gridFilteredRows}
          allData={gridRows}
          columns={columns}
          filters={filters}
          setFilters={setFilters}
          columnOrder={columnOrder}
          hiddenColumns={hiddenColumns}
          frozenColumns={['panel']}
          sectionGroups={sectionGroups}
        />
      </div>

      <ColumnSettingsModal
        isOpen={showColSettings}
        onClose={() => setShowColSettings(false)}
        columns={columns}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        frozenColumns={['panel']}
        onApply={handleColSettingsApply}
      />
    </div>
  );
};

export default DistanceApproxPage;
